// RecipeCostAnalysis.jsx - Düzeltilmiş Versiyon
import React, { useState, useEffect } from 'react';
import { FaFilter, FaUndoAlt } from 'react-icons/fa';
import api from '../services/api.service';
import './RecipeCostAnalysis.css';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const RecipeCostAnalysis = () => {
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [chartType, setChartType] = useState('profit-margin');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [recipesRes, categoriesRes] = await Promise.all([
          api.getRecipes(),
          api.getMenuCategories({ isActive: true })
        ]);
        
        setRecipes(recipesRes.data);
        setCategories(categoriesRes.data);
        setError(null);
      } catch (err) {
        console.error("Veri yükleme hatası:", err);
        setError("Veriler yüklenirken bir hata oluştu.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Filter recipes by category
  const filteredRecipes = recipes.filter(recipe => {
    if (selectedCategory && recipe.menuItem.category.id.toString() !== selectedCategory) {
      return false;
    }
    return true;
  });
  
  // Prepare chart data
  const prepareChartData = () => {
    if (chartType === 'profit-margin') {
      // Sort by profit margin percentage
      return [...filteredRecipes]
        .sort((a, b) => {
          const costA = parseFloat(a.calculatedCost) || 0;
          const costB = parseFloat(b.calculatedCost) || 0;
          const priceA = parseFloat(a.menuItem.price) || 0;
          const priceB = parseFloat(b.menuItem.price) || 0;
          
          const marginA = priceA - costA;
          const marginB = priceB - costB;
          const percentA = priceA > 0 ? (marginA / priceA) * 100 : 0;
          const percentB = priceB > 0 ? (marginB / priceB) * 100 : 0;
          
          return percentB - percentA;
        })
        .slice(0, 10) // Top 10
        .map(recipe => {
          const cost = parseFloat(recipe.calculatedCost) || 0;
          const price = parseFloat(recipe.menuItem.price) || 0;
          const margin = price - cost;
          const percentage = price > 0 ? (margin / price) * 100 : 0;
          
          return {
            name: recipe.menuItem.name,
            cost: parseFloat(cost.toFixed(2)),
            price: parseFloat(price.toFixed(2)),
            margin: parseFloat(margin.toFixed(2)),
            percentage: parseFloat(percentage.toFixed(2))
          };
        });
    } else if (chartType === 'cost-breakdown') {
      // Calculate total cost by category
      const categoryMap = {};
      
      filteredRecipes.forEach(recipe => {
        const categoryId = recipe.menuItem.category.id;
        const categoryName = recipe.menuItem.category.name;
        const cost = parseFloat(recipe.calculatedCost) || 0;
        
        if (!categoryMap[categoryId]) {
          categoryMap[categoryId] = {
            name: categoryName,
            totalCost: 0,
            count: 0
          };
        }
        
        categoryMap[categoryId].totalCost += cost;
        categoryMap[categoryId].count += 1;
      });
      
      return Object.values(categoryMap).map(category => ({
        name: category.name,
        value: parseFloat(category.totalCost.toFixed(2)),
        count: category.count,
        averageCost: parseFloat((category.totalCost / category.count).toFixed(2))
      }));
    } else if (chartType === 'price-vs-cost') {
      // Price vs cost comparison for all items
      return [...filteredRecipes]
        .sort((a, b) => {
          const costA = parseFloat(a.calculatedCost) || 0;
          const costB = parseFloat(b.calculatedCost) || 0;
          const priceA = parseFloat(a.menuItem.price) || 0;
          const priceB = parseFloat(b.menuItem.price) || 0;
          
          const marginA = priceA - costA;
          const marginB = priceB - costB;
          
          return marginB - marginA;
        })
        .slice(0, 10) // Top 10
        .map(recipe => {
          return {
            name: recipe.menuItem.name,
            cost: parseFloat(recipe.calculatedCost) || 0,
            price: parseFloat(recipe.menuItem.price) || 0
          };
        });
    }
    
    return [];
  };
  
  const chartData = prepareChartData();
  
  // Custom colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1', '#a4de6c', '#d0ed57'];
  
  // Calculate summary statistics
  const calculateSummaryStats = () => {
    if (filteredRecipes.length === 0) {
      return {
        avgCost: 0,
        avgPrice: 0,
        avgMargin: 0,
        avgMarginPercent: 0,
        highestMargin: { name: '-', value: 0 },
        lowestMargin: { name: '-', value: 0 }
      };
    }
    
    let totalCost = 0;
    let totalPrice = 0;
    let highestMargin = { value: -Infinity };
    let lowestMargin = { value: Infinity };
    
    filteredRecipes.forEach(recipe => {
      const cost = parseFloat(recipe.calculatedCost) || 0;
      const price = parseFloat(recipe.menuItem.price) || 0;
      const margin = price - cost;
      const marginPercent = price > 0 ? (margin / price) * 100 : 0;
      
      totalCost += cost;
      totalPrice += price;
      
      if (margin > highestMargin.value) {
        highestMargin = {
          name: recipe.menuItem.name,
          value: margin,
          percent: marginPercent
        };
      }
      
      if (margin < lowestMargin.value) {
        lowestMargin = {
          name: recipe.menuItem.name,
          value: margin,
          percent: marginPercent
        };
      }
    });
    
    const count = filteredRecipes.length;
    const avgCost = count > 0 ? totalCost / count : 0;
    const avgPrice = count > 0 ? totalPrice / count : 0;
    const avgMargin = avgPrice - avgCost;
    const avgMarginPercent = avgPrice > 0 ? (avgMargin / avgPrice) * 100 : 0;
    
    return {
      avgCost,
      avgPrice,
      avgMargin,
      avgMarginPercent,
      highestMargin,
      lowestMargin
    };
  };
  
  const stats = calculateSummaryStats();
  
  // Reset filter
  const resetFilter = () => {
    setSelectedCategory('');
  };
  
  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="tooltip-entry" style={{ color: entry.color }}>
              {entry.name}: ₺{entry.value.toFixed(2)}
            </p>
          ))}
        </div>
      );
    }
    
    return null;
  };
  
  // Custom pie chart label
  const renderPieChartLabel = ({ name, value, percent }) => {
    return `${name}: ₺${value.toFixed(2)} (${(percent * 100).toFixed(0)}%)`;
  };
  
  return (
    <div className="cost-analysis">
      {error && <div className="error-message">{error}</div>}
      
      <div className="filter-container">
        <div className="filter-group">
          <label className="filter-label" htmlFor="category-filter">Kategori Filtrele</label>
          <select
            id="category-filter"
            className="filter-select"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            disabled={loading}
          >
            <option value="">Tüm Kategoriler</option>
            {categories.map(category => (
              <option key={category.id} value={category.id.toString()}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label className="filter-label" htmlFor="chart-type">Grafik Türü</label>
          <select
            id="chart-type"
            className="filter-select"
            value={chartType}
            onChange={(e) => setChartType(e.target.value)}
            disabled={loading}
          >
            <option value="profit-margin">Kâr Marjı Analizi</option>
            <option value="cost-breakdown">Kategori Bazlı Maliyet Dağılımı</option>
            <option value="price-vs-cost">Fiyat vs Maliyet Karşılaştırması</option>
          </select>
        </div>
        
        <button 
          className="reset-button"
          onClick={resetFilter}
          disabled={!selectedCategory || loading}
        >
          <FaUndoAlt className="button-icon" />
          Filtreyi Temizle
        </button>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Veriler yükleniyor...</p>
        </div>
      ) : filteredRecipes.length === 0 ? (
        <div className="info-message">
          {selectedCategory 
            ? 'Seçilen kategori için reçete bulunamadı.' 
            : 'Henüz reçete eklenmemiş.'}
        </div>
      ) : (
        <>
          {/* Summary Stats */}
          <div className="summary-stats">
            <div className="stat-card">
              <h3 className="stat-title">Ortalama Kâr Marjı</h3>
              <div className="stat-value">₺{stats.avgMargin.toFixed(2)}</div>
              <div className="stat-percent">%{stats.avgMarginPercent.toFixed(2)}</div>
            </div>
            
            <div className="stat-card">
              <h3 className="stat-title">En Yüksek Kâr Marjı</h3>
              <div className="stat-value positive">₺{stats.highestMargin.value.toFixed(2)}</div>
              <div className="stat-label">{stats.highestMargin.name}</div>
              <div className="stat-percent">%{stats.highestMargin.percent.toFixed(2)}</div>
            </div>
            
            <div className="stat-card">
              <h3 className="stat-title">En Düşük Kâr Marjı</h3>
              <div className="stat-value negative">₺{stats.lowestMargin.value.toFixed(2)}</div>
              <div className="stat-label">{stats.lowestMargin.name}</div>
              <div className="stat-percent">%{stats.lowestMargin.percent.toFixed(2)}</div>
            </div>
          </div>
          
          {/* Chart Area */}
          <div className="chart-container">
            {chartType === 'profit-margin' && (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45} 
                    textAnchor="end" 
                    height={100} 
                  />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="cost" name="Maliyet" fill="#8884d8" />
                  <Bar dataKey="price" name="Satış Fiyatı" fill="#82ca9d" />
                  <Bar dataKey="margin" name="Kâr Marjı" fill="#ff7300" />
                </BarChart>
              </ResponsiveContainer>
            )}
            
            {chartType === 'cost-breakdown' && (
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    outerRadius={150}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                    label={renderPieChartLabel}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `₺${value.toFixed(2)}`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
            
            {chartType === 'price-vs-cost' && (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45} 
                    textAnchor="end" 
                    height={100} 
                  />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="cost" name="Maliyet" fill="#8884d8" />
                  <Bar dataKey="price" name="Satış Fiyatı" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          
          {/* Data Table */}
          <div className="data-table-container">
            <h3 className="table-title">Detaylı Maliyet Raporu</h3>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Ürün Adı</th>
                    <th>Kategori</th>
                    <th>Maliyet</th>
                    <th>Satış Fiyatı</th>
                    <th>Kâr Marjı</th>
                    <th>Kâr Yüzdesi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecipes.map(recipe => {
                    const cost = parseFloat(recipe.calculatedCost) || 0;
                    const price = parseFloat(recipe.menuItem.price) || 0;
                    const margin = price - cost;
                    const percentage = price > 0 ? (margin / price) * 100 : 0;
                    
                    return (
                      <tr key={recipe.id}>
                        <td>{recipe.menuItem.name}</td>
                        <td>{recipe.menuItem.category?.name || 'Kategorisiz'}</td>
                        <td>₺{cost.toFixed(2)}</td>
                        <td>₺{price.toFixed(2)}</td>
                        <td className={margin >= 0 ? 'positive' : 'negative'}>
                          ₺{margin.toFixed(2)}
                        </td>
                        <td className={percentage >= 0 ? 'positive' : 'negative'}>
                          %{percentage.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={2}>Ortalama</th>
                    <th>₺{stats.avgCost.toFixed(2)}</th>
                    <th>₺{stats.avgPrice.toFixed(2)}</th>
                    <th className={stats.avgMargin >= 0 ? 'positive' : 'negative'}>
                      ₺{stats.avgMargin.toFixed(2)}
                    </th>
                    <th className={stats.avgMarginPercent >= 0 ? 'positive' : 'negative'}>
                      %{stats.avgMarginPercent.toFixed(2)}
                    </th>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RecipeCostAnalysis;